import React, { useEffect, useState } from 'react';
import { GenderSelect } from '../../../components/ui';
import CustomDropdown from '../../../components/ui/CustomDropdown';
import AuthInput from '../../../components/ui/AuthInput';
import AuthPrimaryButton from '../../../components/ui/AuthPrimaryButton';
import {
    modalAlert,
    modalBody,
    modalCloseBtn,
    modalField,
    modalLabel,
    modalNotice,
    modalPrimaryBtn,
    modalRow,
    modalStack,
    modalSubtitle,
    modalTitle,
} from '../../../components/ui/modalDesign';
import { toast } from 'react-toastify';
import UserService from '../../../services/user.service';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../redux/rootReducer';
import { calculateAge } from '../../../components/custom-hooks';
import { X } from 'lucide-react';
import { filterDaysOfTheWeek, restructureTimeData } from "../../../utils/Tools";
import {MIN_AGE_FOR_REG, filterOpenCohorts, resolvePackageForChild} from "../../../utils/constants";
import { setRenewal, setResume } from '../../../redux/reducers/userSlice';

type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece];

export interface ChildRegProps {
    firstName: string,
    lastName: string,
    age: number,
    gender: string,
}

const initialValues = {
    firstName: '',
    lastName: '',
    age: 0,
    gender: 'male',
}

/** Child has completed at least one program (so "Add Program" is valid) */
function hasCompletedProgram(child: any): boolean {
    const programs = child?.programs;
    if (!Array.isArray(programs) || programs.length === 0) return false;
    return programs.some((p: any) => p.isCompleted === true);
}

interface Props {
    handleNext: () => void;
    setChildInfo: (data: any) => void;
    closeModalOnOutsideClick: (data: boolean) => void;
    closeRegModal: () => void;
    onDuplicateChildAddProgram?: (child: any) => void;
    onDuplicateChildResume?: (child: any) => void;
    /** When true, only create the child profile (no day/time/cohort/package). */
    childOnly?: boolean;
    onChildOnlyComplete?: () => void;
}


const inputFieldStyle = modalField;
const labelStyle = modalLabel;

//global time storage
let staticTime: any = []
let cohortKeep: any = []

export default function NewRegModal({
    handleNext,
    setChildInfo,
    closeModalOnOutsideClick,
    closeRegModal,
    onDuplicateChildAddProgram,
    onDuplicateChildResume,
    childOnly = false,
    onChildOnlyComplete,
}: Props) {
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);
    const userService = new UserService();
    const dispatch = useDispatch();

    const [value, onChange] = useState<Value>(new Date());

    const [formData, setFormData] = useState(initialValues);
    const [dayTime, setDayTime] = useState<any>([]);
    const [dayArr, setDayArr] = useState<{ name: string; value: number; }[] | []>([]);
    const [selectedDay, setSelectedDay] = useState<{ name: string, value: number } | null>(null);
    const [timeArr, setTimeArr] = useState<{ name: string; value: number; }[] | []>([]);
    const [selectedTime, setSelectedTime] = useState<{ name: string, value: number } | null>(null);
    const [loading, setLoading] = useState(false);
    const [cohortId, setCohortId] = useState(0);
    const [cohortArr, setCohortArr] = useState<{ name: string; value: number; }[] | []>([]);
    //new states
    const [packages, setPackages] = useState<any>([])
    const [selectedPackage, setSelectedPackage] = useState<any>()
    const [selectedSubPackage, setSelectedSubPackageId] = useState<any>(null)
    const [duplicateChild, setDuplicateChild] = useState<any>(null)


    // load all available days and their corresponding time for BE
    const getDayTime = async () => {
        try {
            const response = await userService.getDayTime();
            if (!response.status) {
                toast.error(response.message)
                return;
            }
            setDayTime(response.data);
            staticTime = response.data
            // dispatch(setDayTimeInfo(response.data))
            const responsePkg = await userService.getAllPackages()
            if (!responsePkg.status) {
                toast.error(response.message)
                return;
            }
            setPackages(responsePkg.data)

            const responseCohort = await userService.getCohort()
            if (!responseCohort.status) {
                toast.error(responseCohort.message)
                return;
            }
            cohortKeep = responseCohort.data
            // Filter cohorts immediately based on current age if available
            if (formData.age >= MIN_AGE_FOR_REG) {
                setCohortArr(filterOpenCohorts(cohortKeep))
            } else {
                // If age not set yet, show all cohorts (will be filtered when age is set)
                setCohortArr(responseCohort.data);
            }

            if (response?.data?.length > 0) {
                // extracted dayText and day values and saved them as 'name' and 'value' respectively for ease of use in the custom dropdown component
                setDayArr(filterDaysOfTheWeek(response?.data))
            }
        } catch (err: any) {
            toast.error(err?.message);
        }
        return false;
    };

    const handleDateChange = (e: any) => {
        const age = calculateAge(e);
        setFormData({ ...formData, age });
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault();

        if (childOnly) {
            if (!formData.firstName?.trim() || !formData.lastName?.trim() || formData.age < MIN_AGE_FOR_REG) {
                toast.error(`First name, last name, and age (min ${MIN_AGE_FOR_REG}) are required`);
                return;
            }
            setLoading(true);
            try {
                const response = await userService.addChild(formData);
                setLoading(false);
                if (!response.status) {
                    if (
                        response.message &&
                        (response.message.includes('Child already exist') ||
                            response.message.includes('Child already exists')) &&
                        response.data
                    ) {
                        toast.info('This child already exists. You can select them for the adhoc package.');
                        onChildOnlyComplete?.();
                        return;
                    }
                    toast.error(response.message);
                    return;
                }
                toast.success('Child added successfully');
                onChildOnlyComplete?.();
            } catch (err: any) {
                setLoading(false);
                toast.error(err.message);
            }
            return false;
        }

        if (!selectedDay || !selectedTime || !cohortId || formData.age < MIN_AGE_FOR_REG) {
            toast.error('date, time, valid age and cohort is required!');
            return;
        }

        setLoading(true);
        try {
            const response = await userService.addChild(formData);
            setLoading(false);
            if (!response.status) {
                // Check if this is a duplicate child error
                if (response.message && (response.message.includes("Child already exist") || response.message.includes("Child already exists")) && response.data) {
                    setDuplicateChild(response.data);
                    return;
                }
                toast.error(response.message);
                return;
            }
            const childData = { ...response.data, selectedDay, selectedTime, cohortId, pkgId: selectedPackage.id };
            setChildInfo(childData);
            handleNext();
        } catch (err: any) {
            setLoading(false);
            toast.error(err.message);
        }

        return false;
    };


    const keepModalOpen = () => {
        // if all these are unfilled or empty then closing modal by clicking outside is possible
        if (formData.firstName === '' && formData.lastName === '' && formData.age === 0 && selectedTime === null && selectedDay === null) {
            closeModalOnOutsideClick(true)
        } else {
            closeModalOnOutsideClick(false)
        }
    }


    useEffect(() => {
        if (childOnly) {
            keepModalOpen();
            return;
        }
        getDayTime();
        keepModalOpen();
    }, []);

    useEffect(() => {
        keepModalOpen();
    }, [formData])

    // Fetch full child when duplicate detected so we have programs for Add Program vs Resume check
    useEffect(() => {
        if (!duplicateChild?.id) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await userService.getAllChildren();
                if (cancelled || !res.status) return;
                const full = res.data.find((c: any) => c.id === duplicateChild.id);
                if (full && !cancelled) setDuplicateChild(full);
            } catch (_) {}
        })();
        return () => { cancelled = true; };
    }, [duplicateChild?.id])

    useEffect(() => {
        if (selectedDay) {
            const timeX = dayTime?.filter((item: any) => item.dayText === selectedDay.name);
            const tdx = timeX[0].times;
            // extracted timeText and time values and saved them as 'name' and 'value' respectively for ease of use in the custom dropdown component
            let arr = []
            for (let i = 0; i < tdx.length; i++) {
                let name = tdx[i].timeText;
                let value = tdx[i].time;
                let _dy = { name, value };
                arr.push(_dy);
            }
            setTimeArr(arr)
        }
    }, [selectedDay]);

    //filter time based on age
    useEffect(() => {
        if (formData.age >= MIN_AGE_FOR_REG) {
            const pkg = resolvePackageForChild(packages, formData.age, 1)
            if (pkg) {
                setSelectedPackage(pkg)
                localStorage.setItem("package", JSON.stringify(pkg))
                //offload remote times
                if (pkg?.timeGroup) {
                    //this has a unique time
                    const remTimeData = restructureTimeData(pkg?.timeGroup?.times)
                    setDayArr(filterDaysOfTheWeek(remTimeData))
                    setDayTime(remTimeData)
                } else {
                    //populate normal time
                    setDayTime(staticTime)
                    setDayArr(filterDaysOfTheWeek(staticTime))
                }
            } else if (packages.length > 0) {
                toast.warn("Please try again next time. Program within the age range does not exist.")
            }
        }
    }, [formData.age, packages]);

    // Filter cohorts based on age - separate effect to ensure it always runs
    useEffect(() => {
        if (formData.age >= MIN_AGE_FOR_REG && cohortKeep && cohortKeep.length > 0) {
            setCohortArr(filterOpenCohorts(cohortKeep))
        }
    }, [formData.age]);


    return (
       <div className="relative w-full">
         <form className={modalBody} onSubmit={handleSubmit}>
            <button type="button" aria-label="Close" className={modalCloseBtn} onClick={closeRegModal}>
                <X size={18} />
            </button>

            <div className="mb-5 pr-8 text-center sm:mb-6">
                <h1 className={modalTitle}>{childOnly ? 'Add Child' : 'Register Child'}</h1>
                <p className={modalSubtitle}>
                    {childOnly
                        ? 'Add your child\'s details, then return to select them for the adhoc package.'
                        : 'Add your child\'s details to enroll in a live class program.'}
                </p>
            </div>

            {!childOnly && (
            <div className={`${modalNotice} mb-4`}>
                Every child uses the parent&apos;s timezone:{' '}
                <span className="font-semibold text-emerald-700">
                    {userInfo.timeOffset > 0 && '+'}{userInfo.timezone} ({userInfo?.timeOffset})
                </span>
            </div>
            )}

            {!childOnly && duplicateChild && (
                <div className={`${modalAlert} mb-4`}>
                    <p className="mb-2 text-[14px] font-semibold">Child already exists!</p>
                    {hasCompletedProgram(duplicateChild) ? (
                        <>
                            <p className="mb-3">
                                A child named &quot;{duplicateChild.firstName} {duplicateChild.lastName}&quot; already
                                exists and has completed a program. Add another program for this child instead.
                            </p>
                            <button
                                type="button"
                                onClick={async () => {
                                    try {
                                        const allChildrenResponse = await userService.getAllChildren();
                                        const fullChildData = allChildrenResponse.status
                                            ? allChildrenResponse.data.find((c: any) => c.id === duplicateChild.id)
                                            : null;
                                        const childData = fullChildData || { ...duplicateChild, programs: duplicateChild.programs || [] };
                                        if (onDuplicateChildAddProgram) {
                                            onDuplicateChildAddProgram(childData);
                                        } else {
                                            dispatch(setRenewal(childData));
                                            closeRegModal();
                                        }
                                    } catch (_) {
                                        const childWithStructure = { ...duplicateChild, programs: duplicateChild.programs || [] };
                                        if (onDuplicateChildAddProgram) onDuplicateChildAddProgram(childWithStructure);
                                        else { dispatch(setRenewal(childWithStructure)); closeRegModal(); }
                                    }
                                }}
                                className={modalPrimaryBtn}
                            >
                                Add a Program
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="mb-3">
                                A child named &quot;{duplicateChild.firstName} {duplicateChild.lastName}&quot; already
                                exists but has not finished registration. Resume to complete it.
                            </p>
                            <button
                                type="button"
                                onClick={async () => {
                                    try {
                                        const allChildrenResponse = await userService.getAllChildren();
                                        const fullChildData = allChildrenResponse.status
                                            ? allChildrenResponse.data.find((c: any) => c.id === duplicateChild.id)
                                            : null;
                                        const childData = fullChildData || { ...duplicateChild, programs: duplicateChild.programs || [] };
                                        if (onDuplicateChildResume) {
                                            onDuplicateChildResume(childData);
                                        } else {
                                            closeRegModal();
                                            setTimeout(() => dispatch(setResume(childData)), 100);
                                        }
                                    } catch (_) {
                                        const childWithStructure = { ...duplicateChild, programs: duplicateChild.programs || [] };
                                        if (onDuplicateChildResume) onDuplicateChildResume(childWithStructure);
                                        else { closeRegModal(); setTimeout(() => dispatch(setResume(childWithStructure)), 100); }
                                    }
                                }}
                                className="inline-flex w-full items-center justify-center rounded-[14px] bg-amber-600 px-5 py-3.5 text-[15px] font-semibold text-white transition hover:bg-amber-700"
                            >
                                Resume Registration
                            </button>
                        </>
                    )}
                </div>
            )}

            <div className={modalStack}>
                <div className={modalRow}>
                    <AuthInput
                        label="First name"
                        type="text"
                        placeholder="John"
                        required
                        value={formData.firstName}
                        onChange={(e: any) => setFormData({ ...formData, firstName: e.target.value })}
                    />
                    <AuthInput
                        label="Last name"
                        type="text"
                        placeholder="Doe"
                        required
                        value={formData.lastName}
                        onChange={(e: any) => setFormData({ ...formData, lastName: e.target.value })}
                    />
                </div>

                <div className={modalRow}>
                    <div>
                        <label className={labelStyle}>Gender</label>
                        <GenderSelect
                            handleGenderChange={(item: string) => setFormData({ ...formData, gender: item })}
                            className={inputFieldStyle}
                        />
                    </div>
                    <div>
                        <label className={labelStyle}>Child age (min {MIN_AGE_FOR_REG})</label>
                        <select
                            required
                            className={inputFieldStyle}
                            onChange={(item: any) => {
                                const ageValue = Number(item.target.value);
                                setFormData({ ...formData, age: ageValue });
                            }}
                            value={formData.age}
                        >
                            <option value={0}>Choose age</option>
                            {Array.from(Array(31).keys()).slice(6, 31).map((i) => (
                                <option key={i} value={i}>{i}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {!childOnly && (
                <>
                <div>
                    <label className={labelStyle}>Program</label>
                    <p className={`${inputFieldStyle} text-[#8b9aab]`}>
                        {selectedPackage?.title || 'Select age to see program'}
                    </p>
                </div>

                <div>
                    <label className={labelStyle}>Lesson day</label>
                    <CustomDropdown
                        className={inputFieldStyle}
                        handleChange={(data: { name: string, value: number }) => setSelectedDay(data)}
                        data={dayArr}
                    />
                </div>

                <div>
                    <label className={labelStyle}>Lesson time</label>
                    <CustomDropdown
                        className={inputFieldStyle}
                        handleChange={(data: { name: string, value: number }) => setSelectedTime(data)}
                        data={timeArr}
                    />
                </div>

                {formData?.age ? (
                    <div>
                        <label className={labelStyle}>Choose cohort</label>
                        <select
                            className={inputFieldStyle}
                            onChange={(e: any) => setCohortId(Number(e.target.value))}
                        >
                            <option value={0}>Choose cohort</option>
                            {cohortArr && cohortArr.map((d: any, i: number) => (
                                <option key={i} value={d.id}>{d.title}</option>
                            ))}
                        </select>
                    </div>
                ) : null}
                </>
                )}

                <AuthPrimaryButton loading={loading} showArrow={!childOnly}>
                    {childOnly ? 'Add child' : 'Next'}
                </AuthPrimaryButton>
            </div>
        </form>
       </div>
    )
}
